import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Star, Users, Zap, Scroll, ChevronRight, ChevronLeft, RotateCw, Home, Sparkles, Clock, Play, Settings, Volume2, ListFilter, Globe } from 'lucide-react';

const App = () => {
  const [gameState, setGameState] = useState('menu'); // menu, settings, playing, finished
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);
  
  // Settings State
  const [useTimer, setUseTimer] = useState(false);
  const [inputDuration, setInputDuration] = useState(5); // Default 5 menit
  const [numQuestions, setNumQuestions] = useState(10); // Default 10 soal
  const [timeLeft, setTimeLeft] = useState(0);
  
  // Sound Ref
  const audioContextRef = useRef(null);

  // Function to play beep sound
  const playBeep = (freq = 440, type = 'sine') => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const audioCtx = new AudioContext();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(freq, audioCtx.currentTime); 
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.00001, audioCtx.currentTime + 0.5);

      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.5);
    } catch (e) {
      console.error("Audio play failed", e);
    }
  };

  // Data Soal (120 Soal - 6 Kategori @20 Soal)
  const allQuestions = {
    ulul_azmi: [
      { q: "Siapakah Nabi yang memiliki mukjizat tongkat membelah lautan?", a: "Nabi Musa AS" },
      { q: "Nabi yang tidak hangus ketika dibakar oleh Raja Namrud adalah?", a: "Nabi Ibrahim AS" },
      { q: "Nabi yang bisa menyembuhkan orang buta dan menghidupkan orang mati atas izin Allah?", a: "Nabi Isa AS" },
      { q: "Nabi Nuh AS dikenal dengan mukjizat membuat apa?", a: "Bahtera (Kapal Besar)" },
      { q: "Siapakah Nabi penutup para nabi (Khatamul Anbiya)?", a: "Nabi Muhammad SAW" },
      { q: "Berapa lama Nabi Nuh AS berdakwah menurut Al-Quran?", a: "950 Tahun" },
      { q: "Kitab yang diturunkan kepada Nabi Musa AS adalah?", a: "Taurat" },
      { q: "Kitab yang diturunkan kepada Nabi Isa AS adalah?", a: "Injil" },
      { q: "Julukan 'Khalilullah' (Kekasih Allah) diberikan kepada Nabi?", a: "Nabi Ibrahim AS" },
      { q: "Siapakah raja zalim yang menjadi musuh Nabi Musa AS?", a: "Fir'aun" },
      { q: "Berapa jumlah Nabi yang bergelar Ulul Azmi?", a: "5 Nabi" },
      { q: "Anak Nabi Nuh AS yang menolak naik kapal dan tenggelam adalah?", a: "Kan'an" },
      { q: "Nabi Ibrahim AS diperintah Allah untuk menyembelih putranya yang bernama?", a: "Nabi Ismail AS" },
      { q: "Siapakah saudara Nabi Musa AS yang menemaninya berdakwah?", a: "Nabi Harun AS" },
      { q: "Nabi Isa AS lahir dari seorang ibu suci bernama?", a: "Maryam" },
      { q: "Azab yang menimpa kaum Nabi Nuh AS adalah?", a: "Banjir Bandang Besar" },
      { q: "Nabi Isa AS akan turun kembali ke bumi menjelang kiamat untuk membunuh?", a: "Dajjal" },
      { q: "Gelar Ulul Azmi diberikan karena para Nabi tersebut memiliki?", a: "Kesabaran & Ketabahan Luar Biasa" },
      { q: "Nabi Ibrahim AS menghancurkan berhala-berhala menggunakan?", a: "Kapak" },
      { q: "Kaum Nabi Musa AS dikenal dengan sebutan Bani?", a: "Bani Israil" }
    ],
    mukjizat: [
      { q: "Nabi yang dapat berbicara dengan binatang dan jin adalah?", a: "Nabi Sulaiman AS" },
      { q: "Nabi yang mengeluarkan unta betina dari batu besar sebagai mukjizat?", a: "Nabi Saleh AS" },
      { q: "Nabi yang selamat setelah ditelan ikan paus adalah?", a: "Nabi Yunus AS" },
      { q: "Mukjizat Nabi Yusuf AS yang paling terkenal selain ketampanannya adalah?", a: "Menafsirkan Mimpi" },
      { q: "Nabi yang bisa melunakkan besi dengan tangannya adalah?", a: "Nabi Daud AS" },
      { q: "Air yang memancar dari sela-sela jari adalah mukjizat Nabi?", a: "Nabi Muhammad SAW" },
      { q: "Nabi yang diangkat ke langit dan belum wafat hingga kini adalah?", a: "Nabi Isa AS" },
      { q: "Angin yang bisa dikendalikan sebagai kendaraan adalah mukjizat Nabi?", a: "Nabi Sulaiman AS" },
      { q: "Peristiwa terbelahnya bulan adalah mukjizat Nabi?", a: "Nabi Muhammad SAW" },
      { q: "Nabi yang tongkatnya berubah menjadi ular besar adalah?", a: "Nabi Musa AS" },
      { q: "Nabi yang bisa menghidupkan burung dari tanah liat atas izin Allah?", a: "Nabi Isa AS" },
      { q: "Api menjadi dingin adalah mukjizat Nabi?", a: "Nabi Ibrahim AS" },
      { q: "Siapakah Nabi yang tangannya bisa memancarkan cahaya putih?", a: "Nabi Musa AS" },
      { q: "Sumur Zam-zam muncul dari hentakan kaki Nabi?", a: "Nabi Ismail AS (Kecil)" },
      { q: "Hidangan makanan turun dari langit (Al-Maidah) adalah mukjizat Nabi?", a: "Nabi Isa AS" },
      { q: "Baju gamisnya dapat menyembuhkan mata ayahnya yang buta adalah mukjizat Nabi?", a: "Nabi Yusuf AS" },
      { q: "Awan yang menaungi perjalanan adalah tanda kenabian?", a: "Nabi Muhammad SAW" },
      { q: "Nabi Zakariya diberi mukjizat berupa anak di usia sangat tua bernama?", a: "Nabi Yahya AS" },
      { q: "Siapakah Nabi yang mengerti bahasa semut?", a: "Nabi Sulaiman AS" },
      { q: "Al-Quran adalah mukjizat terbesar sepanjang masa milik Nabi?", a: "Nabi Muhammad SAW" }
    ],
    sirah: [
      { q: "Di kota manakah Nabi Muhammad SAW dilahirkan?", a: "Mekkah" },
      { q: "Tahun kelahiran Nabi Muhammad SAW disebut tahun?", a: "Tahun Gajah" },
      { q: "Wahyu pertama turun kepada Nabi Muhammad SAW di mana?", a: "Gua Hira" },
      { q: "Peristiwa perjalanan Nabi dari Masjidil Haram ke Masjidil Aqsa disebut?", a: "Isra Miraj" },
      { q: "Siapakah wanita pertama yang dinikahi Nabi Muhammad SAW?", a: "Khadijah binti Khuwailid" },
      { q: "Masjid pertama yang dibangun oleh Nabi Muhammad SAW adalah?", a: "Masjid Quba" },
      { q: "Perang besar pertama dalam Islam bernama perang?", a: "Perang Badar" },
      { q: "Di usia berapakah Nabi Muhammad SAW diangkat menjadi Rasul?", a: "40 Tahun" },
      { q: "Siapakah paman Nabi yang sangat memusuhi Islam (Abu Lahab/Abu Thalib)?", a: "Abu Lahab" },
      { q: "Apa nama gua tempat Nabi bersembunyi saat dikejar kaum Quraisy ketika Hijrah?", a: "Gua Tsur" },
      { q: "Gelar 'Al-Amin' yang diberikan kepada Nabi Muhammad SAW artinya?", a: "Orang yang Terpercaya" },
      { q: "Siapakah sahabat yang menggantikan tidur di kasur Nabi saat malam Hijrah?", a: "Ali bin Abi Thalib" },
      { q: "Peristiwa pembebasan kota Mekkah dikenal dengan istilah?", a: "Fathu Makkah" },
      { q: "Kendaraan bersayap yang ditunggangi Nabi saat Isra Miraj bernama?", a: "Buraq" },
      { q: "Perjanjian damai antara kaum Muslimin dan Quraisy disebut Perjanjian?", a: "Hudaibiyah" },
      { q: "Perang di mana paman Nabi, Hamzah bin Abdul Muthalib, gugur syahid?", a: "Perang Uhud" },
      { q: "Masjid kedua yang dibangun Nabi dan menjadi pusat pemerintahan di Madinah?", a: "Masjid Nabawi" },
      { q: "Pendeta Nasrani yang melihat tanda kenabian pada Muhammad kecil bernama?", a: "Buhaira" },
      { q: "Tanggal berapakah Nabi Muhammad SAW dilahirkan dalam kalender Hijriyah?", a: "12 Rabiul Awal" },
      { q: "Di kota manakah Nabi Muhammad SAW wafat dan dimakamkan?", a: "Madinah" }
    ],
    keluarga: [
      { q: "Siapakah nama ayah Nabi Muhammad SAW?", a: "Abdullah bin Abdul Muthalib" },
      { q: "Siapakah nama ibu Nabi Muhammad SAW?", a: "Aminah binti Wahb" },
      { q: "Siapakah sahabat yang menemani Nabi saat hijrah ke Madinah?", a: "Abu Bakar Ash-Shiddiq" },
      { q: "Siapakah sepupu sekaligus menantu Nabi Muhammad SAW?", a: "Ali bin Abi Thalib" },
      { q: "Siapakah istri Nabi yang merupakan putri dari Abu Bakar?", a: "Aisyah RA" },
      { q: "Siapakah nama anak Nabi Muhammad SAW yang menikah dengan Ali bin Abi Thalib?", a: "Fatimah Az-Zahra" },
      { q: "Siapakah sahabat yang dijuluki 'Singa Allah'?", a: "Hamzah bin Abdul Muthalib" },
      { q: "Siapakah muadzin pertama dalam Islam?", a: "Bilal bin Rabah" },
      { q: "Siapakah kakek yang mengasuh Nabi Muhammad SAW saat kecil?", a: "Abdul Muthalib" },
      { q: "Siapakah ibu susuan Nabi Muhammad SAW dari Bani Sa'ad?", a: "Halimah As-Sa'diyah" },
      { q: "Siapakah Khalifah kedua yang bergelar Al-Faruq?", a: "Umar bin Khattab" },
      { q: "Siapakah Khalifah ketiga yang menikahi dua putri Nabi?", a: "Utsman bin Affan" },
      { q: "Cucu kesayangan Nabi Muhammad SAW adalah?", a: "Hasan dan Husain" },
      { q: "Istri Nabi yang pertama kali wafat menyusul beliau adalah?", a: "Zainab binti Jahsy" },
      { q: "Putri Nabi yang wafat 6 bulan setelah Nabi wafat?", a: "Fatimah Az-Zahra" },
      { q: "Paman Nabi yang selalu melindunginya namun wafat belum memeluk Islam?", a: "Abu Thalib" },
      { q: "Anak laki-laki Nabi Muhammad SAW dari Mariyah Al-Qibtiyah bernama?", a: "Ibrahim" },
      { q: "Siapakah wanita yang menyusui Nabi sebelum Halimah As-Sa'diyah?", a: "Tsuwaibah" },
      { q: "Sahabat yang sangat kaya dan dermawan, dijamin masuk surga?", a: "Abdurrahman bin Auf" },
      { q: "Sekretaris Nabi yang bertugas mencatat wahyu adalah?", a: "Zaid bin Tsabit" }
    ],
    fakta_quran: [
      { q: "Surah apa yang pertama kali diturunkan?", a: "Al-Alaq (ayat 1-5)" },
      { q: "Surah terpanjang dalam Al-Quran adalah?", a: "Al-Baqarah" },
      { q: "Surah terpendek dalam Al-Quran adalah?", a: "Al-Kautsar" },
      { q: "Surah yang disebut sebagai 'Jantung Al-Quran' adalah?", a: "Yasin" },
      { q: "Berapa jumlah juz dalam Al-Quran?", a: "30 Juz" },
      { q: "Siapakah satu-satunya wanita yang namanya disebut dalam Al-Quran?", a: "Maryam" },
      { q: "Surah yang tidak diawali dengan Bismillah adalah?", a: "At-Taubah" },
      { q: "Ayat Kursi terdapat dalam surah apa?", a: "Al-Baqarah (Ayat 255)" },
      { q: "Wahyu terakhir turun pada saat peristiwa apa?", a: "Haji Wada'" },
      { q: "Berapa lama proses turunnya Al-Quran (kurang lebih)?", a: "23 Tahun (22 tahun 2 bulan 22 hari)" },
      { q: "Surah pembuka dalam Al-Quran disebut?", a: "Al-Fatihah" },
      { q: "Surah penutup (terakhir) dalam urutan mushaf Al-Quran adalah?", a: "An-Nas" },
      { q: "Malaikat yang bertugas menyampaikan wahyu adalah?", a: "Malaikat Jibril" },
      { q: "Berapa jumlah total surah dalam Al-Quran?", a: "114 Surah" },
      { q: "Surah yang menceritakan tentang pemuda yang tertidur di gua disebut?", a: "Al-Kahfi" },
      { q: "Surah yang menceritakan tentang pasukan gajah adalah?", a: "Al-Fil" },
      { q: "Ayat terpanjang dalam Al-Quran adalah ayat tentang utang piutang di Surah?", a: "Al-Baqarah (Ayat 282)" },
      { q: "Surah yang nilainya sebanding dengan sepertiga Al-Quran adalah?", a: "Al-Ikhlas" },
      { q: "Al-Quran diturunkan pada bulan mulia, yaitu bulan?", a: "Ramadhan" },
      { q: "Golongan surah yang turun di Mekkah disebut?", a: "Makkiyah" }
    ],
    sejarah_nabi_rasul: [
      { q: "Manusia pertama yang diciptakan Allah SWT sekaligus Nabi pertama adalah?", a: "Nabi Adam AS" },
      { q: "Nabi yang pertama kali diajarkan menjahit pakaian dan menulis adalah?", a: "Nabi Idris AS" },
      { q: "Kaum 'Ad yang dibinasakan dengan angin topan adalah umat Nabi?", a: "Nabi Hud AS" },
      { q: "Kaum Tsamud yang dibinasakan dengan suara menggelegar adalah umat Nabi?", a: "Nabi Saleh AS" },
      { q: "Nabi yang diutus kepada kaum Sodom yang memiliki perilaku menyimpang adalah?", a: "Nabi Luth AS" },
      { q: "Siapakah Nabi yang merupakan ayah dari Nabi Yusuf AS dan punya 12 anak?", a: "Nabi Yaqub AS" },
      { q: "Nabi yang pernah menjadi bendahara atau menteri di Mesir setelah dipenjara adalah?", a: "Nabi Yusuf AS" },
      { q: "Nabi Ayyub AS diuji oleh Allah dengan penyakit apa selama bertahun-tahun?", a: "Penyakit Kulit Parah" },
      { q: "Nabi yang berdakwah kepada penduduk Madyan yang suka curang dalam timbangan adalah?", a: "Nabi Syuaib AS" },
      { q: "Nabi Yunus AS diutus untuk berdakwah kepada penduduk kota?", a: "Ninawa (Nineveh)" },
      { q: "Raja Jalut yang bertubuh raksasa berhasil dikalahkan oleh Nabi?", a: "Nabi Daud AS" },
      { q: "Ratu Balqis dari negeri Saba' masuk Islam di masa kenabian siapa?", a: "Nabi Sulaiman AS" },
      { q: "Nabi yang diberi amanah menjaga Maryam binti Imran di Baitul Maqdis adalah?", a: "Nabi Zakariya AS" },
      { q: "Nabi yang mati syahid dibunuh raja zalim demi mempertahankan hukum Allah adalah?", a: "Nabi Yahya AS" },
      { q: "Nabi Ilyas AS berdakwah kepada kaum yang menyembah berhala bernama?", a: "Ba'al" },
      { q: "Saudara kandung Nabi Musa AS yang juga diangkat menjadi Nabi untuk membantunya adalah?", a: "Nabi Harun AS" },
      { q: "Nabi Ismail AS adalah putra Nabi Ibrahim dari istri yang bernama?", a: "Siti Hajar" },
      { q: "Nabi Zulkifli AS dikenal dengan sifat utamanya yaitu?", a: "Sabar & Menepati Janji" },
      { q: "Siapakah Nabi yang dijuluki 'Bapak Para Nabi' (Abul Anbiya)?", a: "Nabi Ibrahim AS" },
      { q: "Kitab Zabur adalah kitab suci yang diturunkan kepada Nabi?", a: "Nabi Daud AS" }
    ]
  };

  const categories = [
    { id: 'ulul_azmi', name: 'Ulul Azmi', icon: <Star className="w-6 h-6 text-yellow-400" />, color: 'bg-emerald-600' },
    { id: 'mukjizat', name: 'Mukjizat Nabi', icon: <Zap className="w-6 h-6 text-blue-400" />, color: 'bg-indigo-600' },
    { id: 'sirah', name: 'Sejarah Nabi Muhammad SAW', icon: <BookOpen className="w-6 h-6 text-green-300" />, color: 'bg-teal-700' },
    { id: 'sejarah_nabi_rasul', name: 'Sejarah Nabi & Rasul', icon: <Globe className="w-6 h-6 text-cyan-200" />, color: 'bg-cyan-700' },
    { id: 'keluarga', name: 'Keluarga & Sahabat', icon: <Users className="w-6 h-6 text-pink-300" />, color: 'bg-rose-600' },
    { id: 'fakta_quran', name: 'Fakta Al-Quran', icon: <Scroll className="w-6 h-6 text-amber-200" />, color: 'bg-slate-700' },
  ];

  // Timer Effect
  useEffect(() => {
    let timerInterval;
    if (gameState === 'playing' && useTimer && timeLeft > 0) {
      timerInterval = setInterval(() => {
        setTimeLeft((prev) => {
          // Trigger sound if time is running out (Last 15 seconds)
          if (prev <= 16 && prev > 1) { // 16 because it triggers before the update to 15
             playBeep(880, 'sine'); // High pitch beep
          }
          
          if (prev <= 1) {
            playBeep(440, 'triangle'); // Final sound
            clearInterval(timerInterval);
            setGameState('finished');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [gameState, useTimer, timeLeft]);

  // Format Time MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const prepareGame = (categoryId) => {
    setSelectedCategory(categoryId);
    setGameState('settings'); // Go to settings first
  };

  const startConfirm = () => {
    // Shuffle questions
    const catQuestions = [...allQuestions[selectedCategory]];
    for (let i = catQuestions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [catQuestions[i], catQuestions[j]] = [catQuestions[j], catQuestions[i]];
    }
    
    // Slice based on selected number of questions
    setShuffledQuestions(catQuestions.slice(0, numQuestions));
    
    setCurrentQuestionIndex(0);
    setIsFlipped(false);
    
    // Timer setup
    if (useTimer) {
      setTimeLeft(inputDuration * 60); // Convert minutes to seconds
    }
    
    setGameState('playing');
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setIsFlipped(false);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
      }, 300); 
    } else {
      setGameState('finished');
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setIsFlipped(false);
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev - 1);
      }, 300);
    }
  };

  const goHome = () => {
    setGameState('menu');
    setSelectedCategory(null);
    setUseTimer(false); // Reset default
    setNumQuestions(10); // Reset to default 10
    setIsFlipped(false);
  };

  // --- Styles ---
  const cardClass = `relative w-full h-80 md:h-96 transition-all duration-500 transform preserve-3d cursor-pointer ${isFlipped ? 'rotate-y-180' : ''}`;
  const faceClass = "absolute w-full h-full backface-hidden rounded-2xl shadow-xl flex flex-col items-center justify-center p-6 text-center border-4";
  const frontClass = `${faceClass} bg-white border-emerald-100 z-20`;
  const backClass = `${faceClass} bg-emerald-600 border-emerald-700 text-white rotate-y-180 z-10`;

  const customStyles = `
    .preserve-3d { transform-style: preserve-3d; }
    .rotate-y-180 { transform: rotateY(180deg); }
    .backface-hidden { backface-visibility: hidden; }
  `;

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col w-full relative">
      <style>{customStyles}</style>
      
      {/* Header - Full Width */}
      <div className="w-full bg-emerald-600 p-4 text-white flex justify-between items-center shadow-md z-50 sticky top-0">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-300" />
          <h1 className="font-bold text-lg tracking-wide">Kuis Kenabian</h1>
        </div>
        {gameState !== 'menu' && (
          <div className="flex items-center gap-3">
             {/* Timer Display in Header */}
            {gameState === 'playing' && useTimer && (
              <div className={`flex items-center gap-1 font-mono text-sm px-3 py-1 rounded-full ${timeLeft <= 15 ? 'bg-red-500 animate-pulse' : timeLeft < 30 ? 'bg-amber-500' : 'bg-emerald-700'}`}>
                {timeLeft <= 15 ? <Volume2 className="w-3 h-3 animate-bounce" /> : <Clock className="w-3 h-3" />}
                <span>{formatTime(timeLeft)}</span>
              </div>
            )}
            <button onClick={goHome} className="p-2 hover:bg-emerald-700 rounded-full transition">
              <Home className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Main Content Area - Full Width Container but constrained content */}
      <div className="flex-1 w-full flex flex-col overflow-y-auto">
        <div className="w-full max-w-7xl mx-auto p-4 md:p-8 flex-1 flex flex-col">
        
        {/* MENU STATE */}
        {gameState === 'menu' && (
          <div className="animate-fade-in w-full">
            <div className="text-center mb-8 mt-4">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">Pilih Topik Kuis</h2>
              <p className="text-gray-500">Ketuk kartu untuk memulai kuis flashcard.</p>
            </div>
            
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => prepareGame(cat.id)}
                  className={`${cat.color} text-white p-6 rounded-2xl shadow-lg hover:brightness-110 active:scale-95 hover:shadow-xl transition-all flex flex-col gap-4 group h-full justify-between border-2 border-transparent hover:border-white/20`}
                >
                  <div className="flex items-start justify-between w-full">
                    <div className="p-3 bg-white/20 rounded-xl">
                      {cat.icon}
                    </div>
                    <ChevronRight className="w-6 h-6 opacity-70 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-left mt-2">
                    <h3 className="font-bold text-xl mb-1">{cat.name}</h3>
                    <p className="text-sm opacity-90">Atur Jumlah Soal</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SETTINGS STATE */}
        {gameState === 'settings' && (
          <div className="w-full h-full flex flex-col items-center justify-center animate-fade-in">
             <div className="w-full max-w-md bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100">
                <div className="flex items-center gap-3 mb-8 text-emerald-600 border-b pb-4">
                  <Settings className="w-7 h-7" />
                  <h2 className="text-2xl font-bold">Pengaturan Kuis</h2>
                </div>
                
                <div className="mb-8 space-y-5">
                  <div className="text-center mb-6">
                    <span className="text-gray-500 text-sm uppercase tracking-wider font-semibold">Kategori Terpilih</span>
                    <p className="text-2xl font-bold text-emerald-700 mt-1">{categories.find(c => c.id === selectedCategory)?.name}</p>
                  </div>
                  
                  {/* Select Number of Questions */}
                  <div className="p-5 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <ListFilter className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-700 font-bold">Jumlah Soal</span>
                    </div>
                    <div className="flex gap-3">
                      <button 
                        onClick={() => setNumQuestions(10)}
                        className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm ${numQuestions === 10 ? 'bg-emerald-600 text-white ring-2 ring-emerald-600 ring-offset-2' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                      >
                        10 Soal
                      </button>
                      <button 
                        onClick={() => setNumQuestions(20)}
                         className={`flex-1 py-3 rounded-xl font-bold transition shadow-sm ${numQuestions === 20 ? 'bg-emerald-600 text-white ring-2 ring-emerald-600 ring-offset-2' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                      >
                        20 Soal
                      </button>
                    </div>
                  </div>

                  {/* Timer Toggle */}
                  <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gray-500" />
                      <span className="text-gray-700 font-bold">Pakai Timer?</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={useTimer} 
                        onChange={(e) => setUseTimer(e.target.checked)} 
                        className="sr-only peer" 
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                    </label>
                  </div>

                  {useTimer && (
                    <div className="animate-fade-in p-5 bg-emerald-50 rounded-xl border border-emerald-100">
                      <label className="block text-sm font-bold text-emerald-800 mb-3">
                        Durasi Waktu (Menit):
                      </label>
                      <div className="flex items-center gap-3">
                         <input 
                          type="number" 
                          min="1" 
                          max="60"
                          value={inputDuration}
                          onChange={(e) => setInputDuration(parseInt(e.target.value) || 1)}
                          className="w-full p-3 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-center font-bold text-xl bg-white"
                        />
                        <span className="text-emerald-700 font-bold">Menit</span>
                      </div>
                    </div>
                  )}
                </div>

                <button 
                  onClick={startConfirm}
                  className="w-full py-4 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-700 active:scale-95 transition flex items-center justify-center gap-2 text-lg"
                >
                  <Play className="w-6 h-6 fill-current" /> Mulai Kuis
                </button>
             </div>
          </div>
        )}

        {/* PLAYING STATE */}
        {gameState === 'playing' && (
          <div className="w-full h-full flex flex-col justify-center animate-fade-in max-w-2xl mx-auto">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between text-sm font-bold text-gray-500 mb-3">
                <span>Soal {currentQuestionIndex + 1} dari {shuffledQuestions.length}</span>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                  {categories.find(c => c.id === selectedCategory)?.name}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-emerald-500 h-3 rounded-full transition-all duration-300" 
                  style={{ width: `${((currentQuestionIndex + 1) / shuffledQuestions.length) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Flashcard Area */}
            <div className="perspective-1000 w-full mb-10">
              <div 
                className={cardClass}
                onClick={() => setIsFlipped(!isFlipped)}
              >
                {/* Front Side (Question) */}
                <div className={frontClass}>
                  <div className="absolute top-6 left-6">
                    <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Pertanyaan</span>
                  </div>
                  <h3 className="text-xl md:text-3xl font-bold text-gray-800 leading-relaxed max-w-lg">
                    {shuffledQuestions[currentQuestionIndex]?.q}
                  </h3>
                  <div className="absolute bottom-8 text-gray-400 text-sm flex items-center gap-2 animate-pulse">
                    <RotateCw className="w-4 h-4" /> Ketuk untuk melihat jawaban
                  </div>
                </div>

                {/* Back Side (Answer) */}
                <div className={backClass}>
                  <div className="absolute top-6 left-6">
                    <span className="bg-emerald-800/30 text-emerald-50 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide border border-emerald-400/30">Jawaban</span>
                  </div>
                  <h3 className="text-2xl md:text-4xl font-bold text-white leading-relaxed max-w-lg">
                    {shuffledQuestions[currentQuestionIndex]?.a}
                  </h3>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="flex gap-4 justify-center items-center">
              {/* Previous Button */}
               <button 
                onClick={prevQuestion}
                disabled={currentQuestionIndex === 0}
                className={`p-4 rounded-2xl shadow-sm border transition flex items-center justify-center ${
                  currentQuestionIndex === 0 
                    ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed" 
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50 active:scale-95 hover:shadow-md"
                }`}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

               <button 
                onClick={() => setIsFlipped(!isFlipped)}
                className="flex-1 py-4 bg-white border border-gray-300 text-gray-700 font-bold text-lg rounded-2xl shadow-sm hover:bg-gray-50 active:scale-95 transition hover:shadow-md"
              >
                {isFlipped ? "Lihat Soal" : "Lihat Jawaban"}
              </button>
              
              <button 
                onClick={nextQuestion}
                className={`flex-1 py-4 font-bold text-lg rounded-2xl shadow-lg active:scale-95 transition flex justify-center items-center gap-2 ${
                  isFlipped 
                    ? "bg-emerald-600 text-white hover:bg-emerald-700 animate-bounce-short hover:shadow-xl" 
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
              >
                {isFlipped ? "Lanjut" : "Skip Soal"} <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* FINISHED STATE */}
        {gameState === 'finished' && (
          <div className="flex flex-col items-center justify-center h-full text-center animate-fade-in max-w-md mx-auto">
            {useTimer && timeLeft === 0 ? (
               <div className="w-28 h-28 bg-red-100 rounded-full flex items-center justify-center mb-8 animate-pulse">
                <Clock className="w-14 h-14 text-red-500" />
              </div>
            ) : (
              <div className="w-28 h-28 bg-emerald-100 rounded-full flex items-center justify-center mb-8">
                <Star className="w-14 h-14 text-emerald-600 fill-current" />
              </div>
            )}

            <h2 className="text-3xl font-bold text-gray-800 mb-3 px-4">Alhamdulillahi Jaza Kumullahu Khoiro</h2>
            
            <p className="text-gray-500 mb-10 text-lg">
              {useTimer && timeLeft === 0 
                ? <span>Waktu habis, tapi usaha kamu mantap! <br/> Kategori: <span className="font-bold text-emerald-600">{categories.find(c => c.id === selectedCategory)?.name}</span></span>
                : <span>Kamu telah menyelesaikan kategori <span className="font-bold text-emerald-600">{categories.find(c => c.id === selectedCategory)?.name}</span>.</span>
              }
            </p>
            
            <button 
              onClick={() => prepareGame(selectedCategory)}
              className="w-full py-4 bg-emerald-600 text-white font-bold rounded-xl shadow-lg hover:bg-emerald-700 active:scale-95 transition mb-4 text-lg"
            >
              Ulangi Kategori Ini
            </button>
            <button 
              onClick={goHome}
              className="w-full py-4 bg-white border border-gray-300 text-gray-700 font-bold rounded-xl shadow-sm hover:bg-gray-50 active:scale-95 transition text-lg"
            >
              Pilih Kategori Lain
            </button>
          </div>
        )}

        </div>
      </div>
      
      {/* Footer Decoration */}
      <div className="w-full p-4 text-center text-xs text-gray-400 bg-gray-50 border-t">
        Dibuat untuk Pengajian Akhir Tahun 2025 - Desa Cikampek Barat
      </div>
    </div>
  );
};

export default App;